import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../components/ui/Button.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Spinner } from '../components/ui/Spinner.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { financeApi } from '../services/financeApi.js';

const EMPTY = {
  companyName: '',
  addressLine1: '',
  zip: '',
  city: '',
  country: 'CH',
  email: '',
  phone: '',
  iban: '',
  bankName: '',
  vatNumber: '',
  vatMethod: 'EFFECTIVE',
  saldoRate1: '',
  saldoRate2: '',
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false,
  smtpUser: '',
  smtpPassword: '',
  smtpFrom: '',
};

export function CompanySettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(false);

  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
  });

  useEffect(() => {
    if (settings.data) {
      setForm({ ...EMPTY, ...settings.data });
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: (body) => financeApi.saveCompanySettings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'company-settings'] });
      setSaved(true);
    },
  });

  const set = (key) => (e) => {
    setSaved(false);
    setForm((p) => ({ ...p, [key]: e.target.value }));
  };

  function submit(e) {
    e.preventDefault();
    const toRate = (v) => (v === '' || v == null ? null : Number(v));
    save.mutate({ ...form, saldoRate1: toRate(form.saldoRate1), saldoRate2: toRate(form.saldoRate2) });
  }

  if (settings.isLoading) {
    return <Card><Spinner /></Card>;
  }

  return (
    <Card>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('settings.company.name')}</span>
          <input className="input" value={form.companyName} onChange={set('companyName')} />
        </label>
        <label className="field">
          <span className="field-label">{t('settings.company.address')}</span>
          <input className="input" value={form.addressLine1} onChange={set('addressLine1')} />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.company.zip')}</span>
            <input className="input" value={form.zip} onChange={set('zip')} />
          </label>
          <label className="field">
            <span className="field-label">{t('settings.company.city')}</span>
            <input className="input" value={form.city} onChange={set('city')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.company.email')}</span>
            <input className="input" type="email" value={form.email} onChange={set('email')} />
          </label>
          <label className="field">
            <span className="field-label">{t('settings.company.phone')}</span>
            <input className="input" value={form.phone} onChange={set('phone')} />
          </label>
        </div>

        <div className="settings-divider">{t('settings.company.bank')}</div>
        <label className="field">
          <span className="field-label">{t('settings.company.iban')}</span>
          <input
            className="input"
            value={form.iban}
            onChange={set('iban')}
            placeholder="CH00 0000 0000 0000 0000 0"
          />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.company.bankName')}</span>
            <input className="input" value={form.bankName} onChange={set('bankName')} />
          </label>
          <label className="field">
            <span className="field-label">{t('settings.company.vat')}</span>
            <input className="input" value={form.vatNumber} onChange={set('vatNumber')} />
          </label>
        </div>

        <div className="settings-divider">{t('settings.vat.title')}</div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.vat.method')}</span>
            <select className="input" value={form.vatMethod} onChange={set('vatMethod')}>
              <option value="EFFECTIVE">{t('settings.vat.effective')}</option>
              <option value="NET_TAX_RATE">{t('settings.vat.saldo')}</option>
            </select>
          </label>
          {form.vatMethod === 'NET_TAX_RATE' && (
            <>
              <label className="field">
                <span className="field-label">{t('settings.vat.saldoRate1')}</span>
                <input className="input" type="number" min="0" max="100" step="0.1" value={form.saldoRate1 ?? ''} onChange={set('saldoRate1')} />
              </label>
              <label className="field">
                <span className="field-label">{t('settings.vat.saldoRate2')}</span>
                <input className="input" type="number" min="0" max="100" step="0.1" value={form.saldoRate2 ?? ''} onChange={set('saldoRate2')} />
              </label>
            </>
          )}
        </div>

        <div className="settings-divider">{t('settings.smtp.title')}</div>
        <p className="muted text-sm" style={{ marginTop: 0 }}>{t('settings.smtp.intro')}</p>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.smtp.host')}</span>
            <input className="input" value={form.smtpHost} onChange={set('smtpHost')} placeholder="smtp.example.com" />
          </label>
          <label className="field">
            <span className="field-label">{t('settings.smtp.port')}</span>
            <input className="input" type="number" min="1" max="65535" value={form.smtpPort} onChange={set('smtpPort')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('settings.smtp.user')}</span>
            <input className="input" value={form.smtpUser} onChange={set('smtpUser')} autoComplete="off" />
          </label>
          <label className="field">
            <span className="field-label">{t('settings.smtp.password')}</span>
            <input
              className="input"
              type="password"
              value={form.smtpPassword}
              onChange={set('smtpPassword')}
              autoComplete="new-password"
              placeholder={settings.data?.smtpPasswordSet ? t('settings.smtp.passwordKeep') : ''}
            />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('settings.smtp.from')}</span>
          <input className="input" type="email" value={form.smtpFrom} onChange={set('smtpFrom')} placeholder="rechnung@example.com" />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input
            type="checkbox"
            checked={form.smtpSecure}
            onChange={(e) => {
              setSaved(false);
              setForm((p) => ({ ...p, smtpSecure: e.target.checked }));
            }}
          />
          <span className="field-label" style={{ margin: 0 }}>{t('settings.smtp.secure')}</span>
        </label>

        {save.isError && <div className="alert danger">{save.error?.message || t('common.loadError')}</div>}
        {saved && !save.isPending && <div className="alert success">{t('settings.company.saved')}</div>}

        <div className="page-actions">
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
